// packages/api-client/src/services/public/agentsVersionsService.ts
import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type { 
  ServiceResponse, 
  AgentVersion, 
  AgentVersionOverrides, 
  UUID,
  AgentProfile,
  AvatarsBlock,
  ListVersionsParams,
  PaginatedAgentVersions
} from '@dopomogai/types';

const VERSIONS_PREFIX = '/agent_versions';
const NEW_VERSIONS_PREFIX = '/versions';

// Define payload types directly related to this service
interface AgentVersionCreatePayload {
  agent_id: UUID;
  overrides: AgentVersionOverrides;
  version_name?: string;
  tags?: string[];
  description?: string;
}

interface AgentVersionUpdatePayload {
  overrides?: AgentVersionOverrides;
  version_name?: string;
  tags?: string[];
  description?: string;
  // Optional visitor ownership for anonymous users
  visitor_id?: string;
}

interface ForkVersionPayload {
  visitor_id?: string;
}

export const agentVersionsService = {
  /**
   * Creates a new agent version.
   * @param payload The data for creating a new agent version.
   * @returns A promise that resolves with the created AgentVersion data or an API error.
   */
  createAgentVersion: (payload: AgentVersionCreatePayload): ServiceResponse<AgentVersion> => {
    return getApiClient().post<AgentVersion>(`${NEW_VERSIONS_PREFIX}/agents/${encodeURIComponent(payload.agent_id)}`, {
      overrides: payload.overrides,
      version_name: payload.version_name,
      tags: payload.tags,
      description: payload.description
    })
      .then(res => res.data)
      .catch(err => handleApiError(err, `creating agent version for agent '${payload.agent_id}'`));
  },

  /**
   * Fetches a single, specific agent version by its UUID.
   * @param versionId The UUID of the agent version to retrieve.
   * @returns A promise that resolves with the AgentVersion data or an API error.
   */
  getAgentVersion: (versionId: UUID): ServiceResponse<AgentVersion> => {
    return getApiClient().get<AgentVersion>(`${NEW_VERSIONS_PREFIX}/${versionId}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `fetching agent version '${versionId}'`));
  },

  /**
   * Lists agent versions with flexible filtering and pagination.
   * @param params Optional parameters for filtering and pagination
   * @returns A promise that resolves with paginated AgentVersion data or an API error.
   */
  listAgentVersions: (params: ListVersionsParams = {}): ServiceResponse<PaginatedAgentVersions> => {
    const qs = new URLSearchParams();
    
    if (params.agent_id) qs.set('agent_id', params.agent_id);
    if (params.profile_id) qs.set('profile_id', params.profile_id);
    if (params.is_public !== undefined) qs.set('is_public', String(params.is_public));
    if (typeof params.limit === 'number') qs.set('limit', String(params.limit));
    if (typeof params.offset === 'number') qs.set('offset', String(params.offset));
    if (params.sort) qs.set('sort', params.sort);
    
    const url = qs.toString() ? `${NEW_VERSIONS_PREFIX}?${qs}` : NEW_VERSIONS_PREFIX;

    return getApiClient()
      .get<PaginatedAgentVersions>(url)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'listing agent versions'));
  },

  /**
   * Updates an existing agent version. Used for auto-saving from the builder.
   * @param versionId The UUID of the agent version to update.
   * @param payload The fields to update, typically the `overrides` object, and optional visitor_id.
   * @returns A promise that resolves with the updated AgentVersion data or an API error.
   */
  updateAgentVersion: (versionId: UUID, payload: AgentVersionUpdatePayload): ServiceResponse<AgentVersion> => {
    return getApiClient().patch<AgentVersion>(`${NEW_VERSIONS_PREFIX}/${versionId}`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, `updating agent version '${versionId}'`));
  },

  /**
   * Deletes an agent version.
   * @deprecated This endpoint is not implemented by the backend. Use alternative methods or wait for backend implementation.
   * @param versionId The UUID of the agent version to delete.
   * @returns A promise that resolves with void or an API error.
   */
  deleteAgentVersion: (versionId: UUID): ServiceResponse<void> => {
    console.warn('deleteAgentVersion is deprecated: backend endpoint not implemented');
    return getApiClient().delete<void>(`${VERSIONS_PREFIX}/${versionId}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `deleting agent version '${versionId}'`));
  },

  /**
   * Duplicates an existing agent version.
   * @deprecated This endpoint is not implemented by the backend. Use alternative methods or wait for backend implementation.
   * @param versionId The UUID of the agent version to duplicate.
   * @param newVersionName Optional name for the duplicated version.
   * @returns A promise that resolves with the new AgentVersion data or an API error.
   */
  duplicateAgentVersion: (versionId: UUID, newVersionName?: string): ServiceResponse<AgentVersion> => {
    console.warn('duplicateAgentVersion is deprecated: backend endpoint not implemented');
    const payload = newVersionName ? { version_name: newVersionName } : {};
    return getApiClient().post<AgentVersion>(`${VERSIONS_PREFIX}/${versionId}/duplicate`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, `duplicating agent version '${versionId}'`));
  },

  /**
   * Sets an agent version as the default version for its agent.
   * @deprecated This endpoint is not implemented by the backend. Use alternative methods or wait for backend implementation.
   * @param versionId The UUID of the agent version to set as default.
   * @returns A promise that resolves with the updated AgentVersion data or an API error.
   */
  setAsDefaultVersion: (versionId: UUID): ServiceResponse<AgentVersion> => {
    console.warn('setAsDefaultVersion is deprecated: backend endpoint not implemented');
    return getApiClient().post<AgentVersion>(`${VERSIONS_PREFIX}/${versionId}/set-default`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `setting agent version '${versionId}' as default`));
  },

  /**
   * Gets the latest version for a specific agent.
   * @param agentId The UUID of the agent to get the latest version for.
   * @returns A promise that resolves with the latest AgentVersion data or an API error.
   */
  getLatestVersion: (agentId: UUID): ServiceResponse<AgentVersion> => {
    // NOTE: Backend /versions/latest returns a paginated list and ignores agent_id.
    // To reliably get the latest for a specific agent, query /versions with filters.
    const qs = new URLSearchParams();
    qs.set('agent_id', agentId);
    qs.set('is_public', 'true');
    qs.set('limit', '1');
    qs.set('offset', '0');
    qs.set('sort', '-created_at');
    const url = `${NEW_VERSIONS_PREFIX}?${qs.toString()}`;
    return getApiClient()
      .get<PaginatedAgentVersions>(url)
      .then(res => {
        const item = res.data?.items?.[0];
        if (!item) {
          throw new Error(`No versions found for agent '${agentId}'`);
        }
        return item as AgentVersion;
      })
      .catch(err => handleApiError(err, `fetching latest version for agent '${agentId}'`));
  },

  /**
   * Gets the default version for a specific agent.
   * @deprecated This endpoint is not implemented by the backend. Use alternative methods or wait for backend implementation.
   * @param agentId The UUID of the agent to get the default version for.
   * @returns A promise that resolves with the default AgentVersion data or an API error.
   */
  getDefaultVersion: (agentId: UUID): ServiceResponse<AgentVersion> => {
    console.warn('getDefaultVersion is deprecated: backend endpoint not implemented');
    return getApiClient().get<AgentVersion>(`${VERSIONS_PREFIX}/default/${agentId}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `fetching default version for agent '${agentId}'`));
  },

  /**
   * Generates a 4-tier avatar set for a version.
   * @param versionId The UUID of the agent version.
   * @param payload The generation options (industry and primary color), optional company hint and visitor_id.
   * @returns A promise that resolves with the AvatarsBlock or an API error.
   */
  generateVersionAvatars: (versionId: UUID, payload: { industry_label: string; primary_hex: string; company_hint?: string; visitor_id?: string }): ServiceResponse<AvatarsBlock> => {
    return getApiClient().post<AvatarsBlock>(`${NEW_VERSIONS_PREFIX}/${versionId}/avatars/generate`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, `generating avatars for version '${versionId}'`));
  },

  /**
   * Creates a personal copy of an existing agent version for the current visitor/user.
   * Targets the new backend prefix /versions.
   */
  forkAgentVersion: (versionId: UUID, payload: ForkVersionPayload): ServiceResponse<AgentVersion> => {
    return getApiClient().post<AgentVersion>(`${NEW_VERSIONS_PREFIX}/${versionId}/fork`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, `forking agent version '${versionId}'`));
  },
};

// Note: The following methods are deprecated because they are not implemented by the backend:
// - deleteAgentVersion  
// - duplicateAgentVersion
// - setAsDefaultVersion
// - getDefaultVersion
// 
// These methods will be removed once the backend provides corresponding endpoints
// or alternative implementations are found.